// frontend/src/livequiz/LiveQuizTeacherPanel.js
import React, { useState, useEffect, useRef } from 'react';
import './livequiz.css';
import API_BASE_URL from '../quiz_management/config';
import AnimatedNumber from './AnimatedNumber';
import { playQuestionSound } from './sound';
import { useNavigate } from 'react-router-dom';
import CountdownTimer from './CountdownTimer';

const WS_BASE_URL = API_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const LiveQuizTeacherPanel = ({ teacherToken }) => {
    const navigate = useNavigate();
    const [quizzes, setQuizzes] = useState([]);
    const [selectedQuizId, setSelectedQuizId] = useState('');
    const [mode, setMode] = useState('poll');
    const [timePerQuestion, setTimePerQuestion] = useState(30);

    const [session, setSession] = useState(null);
    const [status, setStatus] = useState('setup'); // setup / waiting / active / completed
    const [participants, setParticipants] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [error, setError] = useState(null);

    // Buzzer mode state
    const [buzzQueue, setBuzzQueue] = useState([]);
    const [answeredStudentIds, setAnsweredStudentIds] = useState([]);
    const [showAnsweredList, setShowAnsweredList] = useState(false);
    const [resolvedInfo, setResolvedInfo] = useState(null);
    const [timerRunning, setTimerRunning] = useState(true);
    const [roundLog, setRoundLog] = useState([]);
    const [wrongOptions, setWrongOptions] = useState([]);
    const [questionSummaries, setQuestionSummaries] = useState([]);
    const [showSummary, setShowSummary] = useState(false);
    const [activeStudentName, setActiveStudentName] = useState(null);
    const [roundMessage, setRoundMessage] = useState(null);

    // for sound effects persistence so teachers preference sticks
    const [soundOn, setSoundOn] = useState(localStorage.getItem('lqTeacherSoundOff') !== '1');

    // Turn based mode state
    const [turnOrderSet, setTurnOrderSet] = useState(false);

    // Open sessions from DB (resume/close)
    const [openSessions, setOpenSessions] = useState([]);

    // No-timer option (poll / turn_based)
    const [noTimer, setNoTimer] = useState(false);
    const [pointsCorrect, setPointsCorrect] = useState(10);
    const [pointsWrong, setPointsWrong] = useState(0);
    const [answerTime, setAnswerTime] = useState(5);

    const wsRef = useRef(null);
    const sessionCodeRef = useRef(null);
    const reconnectAttempts = useRef(0);
    const reconnectTimer = useRef(null);
    const intentionalClose = useRef(false);

    useEffect(() => {
        loadQuizzes();
        loadOpenSessions();
        return () => {
            intentionalClose.current = true;
            if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
            if (wsRef.current) wsRef.current.close();
        };
    }, []);

    const loadQuizzes = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/quiz/quiz/`);
            const data = await res.json();
            setQuizzes(data);
        } catch (err) {
            setError('Failed to load quizzes');
        }
    };

    const loadOpenSessions = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/livequiz/session/my-open`, {
                headers: { 'Authorization': `Bearer ${teacherToken}` }
            });
            if (!res.ok) return;
            const data = await res.json();
            setOpenSessions(data);
        } catch (err) {
            // Backend unreachable; list stays empty
        }
    };

    const resumeSession = (s) => {
        setSession(s);
        setMode(s.mode);
        setStatus(s.status === 'active' ? 'active' : 'waiting');
        setOpenSessions([]);
        connectWebSocket(s.session_code);
        // sync_state from the backend fills in participants,
        // leaderboard, and the current question.
    };

    const closeOpenSession = async (s) => {
        try {
            const res = await fetch(`${API_BASE_URL}/livequiz/session/${s.session_code}/close`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${teacherToken}` }
            });
            if (res.ok) {
                setOpenSessions(prev => prev.filter(x => x.session_code !== s.session_code));
            }
        } catch (err) {
            setError('Failed to close session');
        }
    };

    const createSession = async () => {
        if (!selectedQuizId) {
            setError('Please select a quiz');
            return;
        }
        setError(null);
        try {
            const res = await fetch(`${API_BASE_URL}/livequiz/session/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${teacherToken}`
                },
                body: JSON.stringify({
                    quiz_id: parseInt(selectedQuizId),
                    mode,
                    time_per_question: (mode !== 'buzzer' && noTimer) ? 0 : parseInt(timePerQuestion),
                    points_correct: parseInt(pointsCorrect) || 0,
                    points_wrong: parseInt(pointsWrong) || 0,
                    answer_time: parseInt(answerTime) || 5
                })
            });
            if (!res.ok) {
                const errData = await res.json();
                setError(typeof errData.detail === 'string' ? errData.detail : 'Failed to create session');
                return;
            }
            const data = await res.json();
            setSession(data);
            setStatus('waiting');
            setOpenSessions([]);
            connectWebSocket(data.session_code);
        } catch (err) {
            setError('Failed to create session');
        }
    };

    const connectWebSocket = (sessionCode) => {
        sessionCodeRef.current = sessionCode;
        intentionalClose.current = false;
        const ws = new WebSocket(`${WS_BASE_URL}/livequiz/ws/teacher/${sessionCode}`);
        wsRef.current = ws;

        ws.onopen = () => {
            reconnectAttempts.current = 0;
            setError(null);
        };

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            handleWsMessage(data);
        };

        ws.onerror = () => {
            // onclose always fires after onerror; reconnection handled there
        };

        ws.onclose = () => {
            if (intentionalClose.current) return;
            if (reconnectAttempts.current < 10) {
                const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 10000);
                reconnectAttempts.current += 1;
                setError('Connection lost. Reconnecting…');
                reconnectTimer.current = setTimeout(
                    () => connectWebSocket(sessionCodeRef.current),
                    delay
                );
            } else {
                setError('Connection lost. Please refresh the page.');
            }
        };
    };

    
    
    const sendAction = (payload) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(payload));
            return true;
        }
        setError('Not connected yet. Reconnecting — please try again in a moment.');
        return false;
    };

    const handleWsMessage = (data) => {
        switch (data.event) {
            case 'student_joined':
                setParticipants(prev => {
                    const exists = prev.find(p => p.student_id === data.student_id);
                    if (exists) return prev;
                    return [...prev, { student_id: data.student_id, name: data.name }];
                });
                break;

            case 'student_left':
                setParticipants(prev => prev.filter(p => p.student_id !== data.student_id));
                break;

            case 'turn_order_set':
                setTurnOrderSet(true);
                break;

            case 'quiz_started':
            case 'next_question':
                if (currentQuestion) {
                    setQuestionSummaries(prev => [...prev, {
                        question: currentQuestion,
                        answeredCount: answeredStudentIds.length,
                        totalParticipants: participants.length,
                        leaderboardSnapshot: leaderboard
                    }]);
                }
                setStatus('active');
                setCurrentQuestion(data.question);
                if (localStorage.getItem('lqTeacherSoundOff') !== '1') playQuestionSound();
                setBuzzQueue([]);
                setActiveStudentName(data.question?.active_student_name ?? null);
                setRoundLog([]);
                setWrongOptions([]);
                setRoundMessage(null);
                setAnsweredStudentIds([]);
                setShowAnsweredList(false);
                setResolvedInfo(null);
                setTimerRunning(true);
                break;

            case 'buzz_received':
                setBuzzQueue(prev => [...prev, { student_id: data.student_id, name: data.name }]);
                break;

            case 'buzzer_locked':
                setActiveStudentName(data.name);
                setRoundMessage(null);
                break;

            case 'turn_changed':
                setActiveStudentName(data.name);
                break;

            case 'no_one_buzzed':
                setRoundMessage('No one buzzed in time.');
                break;

            case 'no_one_answered_correctly':
                setRoundMessage('No one answered correctly.');
                setActiveStudentName(null);
                setTimerRunning(false);
                break;

            case 'answered_update':
                setAnsweredStudentIds(data.answered_student_ids || []);
                break;

            case 'scorecard':
                setLeaderboard(data.leaderboard || []);
                break;

            case 'quiz_ended':
                setStatus('completed');
                setLeaderboard(data.leaderboard || []);
                setCurrentQuestion(null);
                break;

            case 'sync_state':
                if (data.participants) setParticipants(data.participants);
                if (data.leaderboard) setLeaderboard(data.leaderboard);
                if (data.answered_student_ids) setAnsweredStudentIds(data.answered_student_ids);
                if (data.status === 'active' && data.question) {
                    setStatus('active');
                    setCurrentQuestion(data.question);
                } else if (data.status === 'completed') {
                    setStatus('completed');
                    setCurrentQuestion(null);
                }
                break;
            
            case 'time_up':
                setRoundMessage("⏱ Time's up for this question.");
                setTimerRunning(false);
                break;

            case 'answer_update': {
                const msgs = {
                    correct: `✓ ${data.name} answered ${data.selected} — correct!`,
                    wrong: `✗ ${data.name} answered ${data.selected} — wrong`,
                    pass: `${data.name} passed`,
                    timeout: `⏱ ${data.name} ran out of time`
                };
                setRoundLog(prev => [...prev, msgs[data.result]]);
                if (data.result === 'wrong' && data.selected) {
                    setWrongOptions(prev => prev.includes(data.selected) ? prev : [...prev, data.selected]);
                }
                break;
            }
            
            case 'question_resolved':
                setResolvedInfo(data);
                setActiveStudentName(null);
                setTimerRunning(false);
                break;  

            default:
                break;
        }
    };

    const startNewSession = () => {
        intentionalClose.current = true;
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        if (wsRef.current) wsRef.current.close();
        wsRef.current = null;
        setSession(null);
        setStatus('setup');
        setCurrentQuestion(null);
        setLeaderboard([]);
        setParticipants([]);
        setAnsweredStudentIds([]);
        setQuestionSummaries([]);
        setShowSummary(false);
        setRoundMessage(null);
        setError(null);
        loadOpenSessions();
    };

    const setTurnOrder = () => {
        const order = participants.map(p => p.student_id);
        sendAction({ action: 'set_turn_order', turn_order: order });
    };

    const startQuiz = () => {
        if (participants.length === 0) {
            setError('Wait for at least one student to join');
            return;
        }
        if (mode === 'turn_based' && !turnOrderSet) {
            setTurnOrder();
        }
        setError(null);
        sendAction({ action: 'start' });
    };

    const [nextQuestionLoading, setNextQuestionLoading] = useState(false);

    const nextQuestion = () => {
        if (!sendAction({ action: 'next_question' })) return;
        setNextQuestionLoading(true);
        setTimeout(() => setNextQuestionLoading(false), 600);
    };

    const revealAnswer = () => {
        sendAction({ action: 'reveal_answer' });
    };

    const endQuiz = () => {
        if (currentQuestion) {
            setQuestionSummaries(prev => [...prev, {
                question: currentQuestion,
                answeredCount: answeredStudentIds.length,
                totalParticipants: participants.length,
                leaderboardSnapshot: leaderboard
            }]);
        }
        sendAction({ action: 'end' });
    };

    return (
        <div className="livequiz-container">
            <h2 className="livequiz-title">Live Quiz — Teacher Panel</h2>

            {error && <div className="livequiz-error">{error}</div>}

            {status === 'setup' && openSessions.length > 0 && (
                <div className="livequiz-setup-card" style={{ marginBottom: '14px' }}>
                    <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Your Open Sessions</h3>
                    {openSessions.map(s => (
                        <div key={s.session_code} className="livequiz-history-item">
                            <p className="livequiz-history-question">
                                {s.session_code} — {s.quiz_title}
                            </p>
                            <p className="livequiz-history-answer">
                                {s.mode} · {s.status} · {s.participant_count} joined
                                {s.status === 'active' ? ` · on Q${s.current_question_index + 1}` : ''}
                            </p>
                            <div className="livequiz-teacher-controls" style={{ margin: '8px 0 0' }}>
                                <button className="livequiz-secondary-btn" onClick={() => resumeSession(s)}>
                                    Resume
                                </button>
                                <button className="livequiz-danger-btn" onClick={() => closeOpenSession(s)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    ))}
                    <p className="livequiz-empty-hint" style={{ marginBottom: 0 }}>
                        Note: creating a new session will automatically close all sessions above.
                    </p>
                </div>
            )}

            {status === 'setup' && (
                <div className="livequiz-setup-card">
                    <button className="livequiz-secondary-btn" style={{ marginBottom: '12px' }}
                        onClick={() => navigate('/teacher/dashboard')}>
                        ← Back to Dashboard
                    </button>
                    <label className="livequiz-label">Select Quiz</label>
                    <select
                        className="livequiz-select"
                        value={selectedQuizId}
                        onChange={(e) => setSelectedQuizId(e.target.value)}
                    >
                        <option value="">-- Choose a quiz --</option>
                        {quizzes.map(q => (
                            <option key={q.quiz_id} value={q.quiz_id}>{q.quiz_title}</option>
                        ))}
                    </select>

                    <label className="livequiz-label">Mode</label>
                    <div className="livequiz-mode-toggle">
                        <button
                            className={`livequiz-mode-btn ${mode === 'poll' ? 'active' : ''}`}
                            onClick={() => setMode('poll')}
                        >
                            Poll
                        </button>
                        <button
                            className={`livequiz-mode-btn ${mode === 'buzzer' ? 'active' : ''}`}
                            onClick={() => setMode('buzzer')}
                        >
                            Buzzer
                        </button>
                        <button
                            className={`livequiz-mode-btn ${mode === 'turn_based' ? 'active' : ''}`}
                            onClick={() => setMode('turn_based')}
                        >
                            Turn Based
                        </button>
                    </div>

                    <label className="livequiz-label">
                        {mode === 'buzzer' ? 'Buzz Window (seconds)' : 'Time per Question (seconds, optional)'}
                    </label>
                    <input
                        type="number"
                        className="livequiz-input"
                        value={timePerQuestion}
                        onChange={(e) => setTimePerQuestion(e.target.value)}
                        min="5"
                        max="120"
                        disabled={mode !== 'buzzer' && noTimer}
                    />

                    {mode !== 'buzzer' && (
                        <label style={{ display: 'block', margin: '8px 0' }}>
                            <input type="checkbox" checked={noTimer} onChange={(e) => setNoTimer(e.target.checked)} />
                            {' '}No timer (students can answer until you move on)
                        </label>
                    )}

                    {mode === 'buzzer' && (
                        <>
                            <label className="livequiz-label">Answer Time after Buzz (seconds)</label>
                            <input
                                type="number"
                                className="livequiz-input"
                                value={answerTime}
                                onChange={(e) => setAnswerTime(e.target.value)}
                                min="1"
                                max="60"
                            />
                        </>
                    )}

                    <label className="livequiz-label">Points for Correct Answer</label>
                    <input
                        type="number"
                        className="livequiz-input"
                        value={pointsCorrect}
                        onChange={(e) => setPointsCorrect(e.target.value)}
                        min="0"
                    />

                    <label className="livequiz-label">Penalty for Wrong Answer (0 = no penalty)</label>
                    <input
                        type="number"
                        className="livequiz-input"
                        value={pointsWrong}
                        onChange={(e) => setPointsWrong(e.target.value)}
                        min="0"
                    />

                    <button className="livequiz-primary-btn" onClick={createSession}>
                        Create Session
                    </button>
                </div>
            )}

            {status === 'waiting' && session && (
                <div className="livequiz-waiting-card">
                    <div className="livequiz-code-display">
                        <span className="livequiz-code-label">Session Code</span>
                        <span className="livequiz-code-value">{session.session_code}</span>
                    </div>

                    <div className="livequiz-participants-section">
                        <h3>Students Joined ({participants.length})</h3>
                        <div className="livequiz-participants-grid">
                            {participants.map((p, idx) => (
                                <div key={p.student_id} className="livequiz-participant-chip">
                                    {mode === 'turn_based' ? `${idx + 1}. ${p.name}` : p.name}
                                </div>
                            ))}
                            {participants.length === 0 && (
                                <div className="livequiz-empty-hint">Waiting for students to join...</div>
                            )}
                        </div>
                    </div>

                    {mode === 'turn_based' && (
                        <p className="livequiz-empty-hint">
                            Turn order will follow the join order shown above (1, 2, 3...).
                        </p>
                    )}

                    <button className="livequiz-primary-btn" onClick={startQuiz}>
                        Start Quiz
                    </button>
                </div>
            )}

            {status === 'active' && (
                <div className="livequiz-active-card">
                    <div className="livequiz-active-session-badge">
                        Session Code: <strong>{session?.session_code}</strong>
                    </div>
                    <div className="livequiz-progress-bar-wrap">
                        <span>Question {(currentQuestion?.index ?? 0) + 1} of {currentQuestion?.total ?? '-'}</span>
                    </div>

                    {currentQuestion?.text && (
                        <>
                        <CountdownTimer
                            seconds={(mode === 'buzzer' && activeStudentName) ? currentQuestion.answer_time : currentQuestion.time_per_question}
                            resetKey={`${currentQuestion.question_id}-${activeStudentName || ''}`}
                            running={timerRunning}
                        />
                        {activeStudentName && !resolvedInfo && (
                            <div className="livequiz-buzz-order">
                                <p>🎯 Answering now: <strong>{activeStudentName}</strong></p>
                            </div>
                        )}
                        <div className="livequiz-question-display">
                            {(currentQuestion.chapter_name || currentQuestion.topic_name) && (
                                <p className="livequiz-question-context">
                                    {currentQuestion.chapter_name}
                                    {currentQuestion.topic_name ? ` · ${currentQuestion.topic_name}` : ''}
                                </p>
                            )}
                            <p className="livequiz-question-text">{currentQuestion.text}</p>
                            <div className="livequiz-options-grid">
                                {['A', 'B', 'C', 'D'].map(opt => {
                                    const optionText = currentQuestion[`option${opt}`];
                                    if (!optionText) return null;
                                    const isCorrect = resolvedInfo?.correct_answer === opt;
                                    const isWrongPick = wrongOptions.includes(opt);
                                    return (
                                        <div
                                            key={opt}
                                            className="livequiz-option-box"
                                            style={isCorrect
                                                ? { background: '#dcfce7', border: '2px solid #16a34a', fontWeight: 700 }
                                                : isWrongPick
                                                    ? { background: '#fee2e2', border: '2px solid #dc2626' }
                                                    : undefined}
                                        >
                                            {opt}. {optionText} {isCorrect ? '✓' : isWrongPick ? '✗' : ''}
                                        </div>
                                    );
                                            
                                })}
                            </div>
                            {resolvedInfo && (
                                <div style={{ marginTop: '8px' }}>
                                    {!resolvedInfo.resolved_by && <p>Answer revealed</p>}
                                    {resolvedInfo.explanation && <p>💡 <strong>Explanation:</strong> {resolvedInfo.explanation}</p>}
                                </div>
                            )}
                        </div>
                        </>
                    )}

                    {mode === 'buzzer' && (
                        <>
                            {activeStudentName && (
                                <div className="livequiz-buzz-order">
                                    <p><strong>{activeStudentName}</strong> is answering</p>
                                </div>
                            )}
                            {buzzQueue.length > 0 && (
                                <div className="livequiz-buzz-order">
                                    <h4>Buzz Order</h4>
                                    <ol>
                                        {buzzQueue.map((b, idx) => (
                                            <li key={idx}>{b.name}</li>
                                        ))}
                                    </ol>
                                </div>
                            )}
                        </>
                    )}

                    {mode === 'turn_based' && activeStudentName && (
                        <div className="livequiz-buzz-order">
                            <p><strong>{activeStudentName}</strong>'s turn</p>
                        </div>
                    )}

                    {mode === 'poll' && (() => {
                        const pending = participants.filter(p => !answeredStudentIds.includes(p.student_id));
                        const answered = participants.filter(p => answeredStudentIds.includes(p.student_id));
                        return (
                            <div className="livequiz-buzz-order">
                                <h4>Answered ({answered.length} / {participants.length})</h4>

                                {pending.length > 0 ? (
                                    <>
                                        <p style={{ margin: '8px 0 6px', fontSize: '0.85rem', fontWeight: 700, color: '#92400e' }}>
                                            Waiting for ({pending.length}):
                                        </p>
                                        <div className="livequiz-participants-grid">
                                            {pending.map(p => (
                                                <div key={p.student_id} className="livequiz-participant-chip pending">
                                                    {p.name} …
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    participants.length > 0 && (
                                        <p style={{ margin: '8px 0', fontWeight: 700, color: '#15803d' }}>
                                            ✓ All students have answered — safe to move on.
                                        </p>
                                    )
                                )}

                                {answered.length > 0 && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setShowAnsweredList(v => !v)}
                                            style={{
                                                background: 'none', border: 'none', padding: 0,
                                                marginTop: '6px', cursor: 'pointer',
                                                fontSize: '0.85rem', fontWeight: 700, color: '#15803d'
                                            }}
                                        >
                                            {showAnsweredList ? '▾' : '▸'} Answered ({answered.length})
                                        </button>
                                        {showAnsweredList && (
                                            <div className="livequiz-participants-grid" style={{ marginTop: '6px' }}>
                                                {answered.map(p => (
                                                    <div key={p.student_id} className="livequiz-participant-chip answered">
                                                        {p.name} ✓
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })()}

                    {roundMessage && (
                        <div className="livequiz-buzz-order">
                            <p>{roundMessage}</p>
                        </div>
                    )}

                    {roundLog.length > 0 && (
                        <div className="livequiz-buzz-order">
                            {roundLog.map((m, i) => <p key={i} style={{ margin: '2px 0' }}>{m}</p>)}
                        </div>
                    )}

                    <div className="livequiz-teacher-controls">
                        <button
                            className="livequiz-secondary-btn"
                            onClick={() => {
                                const next = !soundOn;
                                setSoundOn(next);
                                localStorage.setItem('lqTeacherSoundOff', next ? '0' : '1');
                            }}
                        >
                            {soundOn ? '🔊 Sound On' : '🔇 Sound Off'}
                        </button>
                        {(mode === 'buzzer' || mode === 'turn_based') && (
                            <button className="livequiz-secondary-btn" onClick={revealAnswer}>
                                Reveal / Skip
                            </button>
                        )}
                        <button className="livequiz-secondary-btn" onClick={nextQuestion} disabled={nextQuestionLoading}>
                            {nextQuestionLoading ? 'Loading...' : 'Next Question'}
                        </button>
                        <button className="livequiz-danger-btn" onClick={endQuiz}>
                            End Quiz
                        </button>
                    </div>

                    {leaderboard.length > 0 && (
                        <div className="livequiz-leaderboard">
                            <h3>Leaderboard</h3>
                            {leaderboard.map((entry, idx) => (
                                <div key={entry.student_id} className="livequiz-leaderboard-row">
                                    <span className="livequiz-rank">#{idx + 1}</span>
                                    <span className="livequiz-lb-name">{entry.name}</span>
                                    <span className="livequiz-lb-score"><AnimatedNumber value={entry.total_score} /> pts</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {status === 'completed' && (
                <div className="livequiz-completed-card">
                    <h3>Quiz Completed</h3>

                    {questionSummaries.length > 0 && (
                        <button className="livequiz-secondary-btn" onClick={() => setShowSummary(!showSummary)} style={{ marginBottom: '14px' }}>
                            {showSummary ? 'Hide' : 'View'} Question-wise Summary
                        </button>
                    )}

                    {showSummary && (
                        <div className="livequiz-history-list">
                            {questionSummaries.map((s, idx) => (
                                <div key={idx} className="livequiz-history-item">
                                    <p className="livequiz-history-question">
                                        Q{idx + 1}: {s.question?.text}
                                    </p>
                                    <p className="livequiz-history-answer">
                                        Answered: {s.answeredCount} / {s.totalParticipants}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="livequiz-leaderboard final">
                        {leaderboard.map((entry, idx) => (
                            <div key={entry.student_id} className={`livequiz-leaderboard-row ${idx === 0 ? 'winner' : ''}`}>
                                <span className="livequiz-rank">#{idx + 1}</span>
                                <span className="livequiz-lb-name">{entry.name}</span>
                                <span className="livequiz-lb-score"><AnimatedNumber value={entry.total_score} /> pts</span>
                            </div>
                        ))}
                    </div>

                    <div className="livequiz-teacher-controls">
                        <button className="livequiz-primary-btn" style={{ marginTop: 0 }} onClick={startNewSession}>
                            + New Session
                        </button>
                        <button className="livequiz-secondary-btn" onClick={() => navigate('/teacher/dashboard')}>
                            ← Back to Dashboard
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiveQuizTeacherPanel;